import { useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";

const shrink = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const s = Math.min(1, 1700 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("bad image"))), "image/jpeg", 0.8);
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

export function ScanDialog({ open, onClose, cats = [], onSaved, fixedCategory, defaultName = "", title }) {
  const ref = useRef(null);
  const [pages, setPages] = useState([]);
  const [cat, setCat] = useState("");
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    try { const blobs = await Promise.all(files.map(shrink)); setPages((p) => [...p, ...blobs.map((b) => ({ b, url: URL.createObjectURL(b) }))].slice(0, 20)); }
    catch { toast.error("That photo couldn't be read. Please try again."); }
  };
  const close = () => { pages.forEach((p) => URL.revokeObjectURL(p.url)); setPages([]); setName(""); onClose(); };
  const save = async () => {
    setBusy(true);
    const fd = new FormData();
    pages.forEach((p, i) => fd.append("files", p.b, `page-${i + 1}.jpg`));
    fd.append("category", fixedCategory || cat || cats[0]?.key);
    fd.append("name", name);
    try { const r = await api.post("/documents/scan", fd); if (!fixedCategory) toast.success(`Saved ${pages.length}-page PDF to your Document Vault`); onSaved(r.data); close(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto" data-testid="scan-dialog">
        <DialogHeader><DialogTitle>{title || "Scan a document with your camera"}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Take a photo of each page. They'll be saved together as one PDF.</p>
        {!fixedCategory && <select value={cat || cats[0]?.key || ""} onChange={(e) => setCat(e.target.value)} className="h-11 border border-slate-300 rounded-md px-2 text-sm bg-white w-full" data-testid="scan-category">
          {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>}
        <Input placeholder="Document name (optional), e.g. City business license" value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="scan-name" />
        <input ref={ref} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={add} data-testid="scan-file-input" />
        <div className="grid grid-cols-3 gap-2" data-testid="scan-pages">
          {pages.map((p, i) => (
            <div key={p.url} className="relative aspect-[3/4] rounded-md overflow-hidden border border-slate-200 bg-slate-50" data-testid={`scan-page-${i}`}>
              <img src={p.url} alt={`Page ${i + 1}`} className="w-full h-full object-cover" />
              <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-white/90 rounded px-1">{i + 1}</span>
              <button onClick={() => setPages(pages.filter((_, j) => j !== i))} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 grid place-items-center" aria-label="Remove page" data-testid={`scan-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <button onClick={() => ref.current.click()} className="aspect-[3/4] rounded-md border-2 border-dashed border-emerald-300 text-emerald-700 flex flex-col items-center justify-center gap-1 text-xs font-semibold hover:bg-emerald-50 transition-colors" data-testid="scan-add-page">
            <Camera className="h-6 w-6" />{pages.length ? "Add page" : "Take photo"}
          </button>
        </div>
        <Button onClick={save} disabled={busy || !pages.length} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700" data-testid="scan-save">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save {pages.length || ""} page{pages.length === 1 ? "" : "s"} as PDF</Button>
      </DialogContent>
    </Dialog>
  );
}
